import React from "react";
import { CircleSlash } from "lucide-react";
import { Input } from "./Input";
import { Select } from "./Select/Select";
import { SelectItems, SelectValue } from "../interfaces";
import useAppServiceFee, {
  APP_SERVICE_FEE_KEY,
  APP_DELIVERY_FEE_KEY,
  APP_DELIVERY_FREE_THRESHOLD_KEY,
  APP_SERVICE_FEE_PLACEHOLDER,
  AppServiceFeeType,
} from "../../../../hooks/useAppServiceFee";
import { Metadata } from "../../../../interfaces/metadata";

export interface AppServiceFeeSettingsProps {
  disabled: boolean;
  emptyValueHint?: string;
  entityMetadata: Metadata[];
  metadata: Record<string, any> | undefined;
  saveMetadata: (metadata: Record<string, any>) => void;
  required?: boolean;
  maxAmount?: number;
}

interface AppFeeFieldProps extends AppServiceFeeSettingsProps {
  feeKey: string;
  label: string;
  hasType: boolean;
}

const FEE_TYPE_OPTIONS: SelectItems[] = [
  { name: "Monto fijo", path: "fixed" },
  { name: "Porcentual", path: "percentage" },
];

const FEE_TYPE_SELECT_VALUES: Record<AppServiceFeeType, SelectValue> = {
  fixed: { label: "Monto fijo", value: "fixed" },
  percentage: { label: "Porcentual", value: "percentage" },
};

/**
 * A single configurable fee: its amount input and, when the fee supports it, a fixed/percentage
 * type selector. Renders nothing when the fee key is not registered for the entity being edited.
 */
const AppFeeField: React.FC<AppFeeFieldProps> = (props) => {
  const { disabled, entityMetadata, metadata, saveMetadata, feeKey, label, hasType, required, maxAmount } =
    props;
  const {
    feeMetadataInfo,
    feeDisplay,
    feeType,
    handleFeeChange,
    handleFeeTypeChange,
  } = useAppServiceFee(entityMetadata, metadata, saveMetadata, feeKey, maxAmount);

  if (!feeMetadataInfo) {
    return null;
  }

  const adornment: string = hasType && feeType === "percentage" ? "%" : "$";
  const error: string = required && feeDisplay === "" ? "La comisión es obligatoria." : "";

  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
      {hasType && (
        <Select
          label="Tipo de comisión"
          placeholder="Selecciona un tipo"
          disabled={disabled}
          isCopyable={disabled}
          items={FEE_TYPE_OPTIONS}
          value={FEE_TYPE_SELECT_VALUES[feeType]}
          onChange={(option: SelectValue) =>
            handleFeeTypeChange(option.value as AppServiceFeeType)
          }
        />
      )}
      <Input
        type="number"
        inputMode="decimal"
        label={label}
        isRequired={required}
        error={error}
        placeholder={APP_SERVICE_FEE_PLACEHOLDER}
        disabled={disabled}
        isCopyable={disabled}
        value={feeDisplay}
        onChange={handleFeeChange}
        leftAdornment={<span className="text-gray-500">{adornment}</span>}
      />
    </div>
  );
};

export const AppServiceFeeSettings: React.FC<AppServiceFeeSettingsProps> = (
  props,
) => {
  const { emptyValueHint, entityMetadata } = props;

  const hasFeeConfig: boolean = entityMetadata.some(
    (option: Metadata) => option.key === APP_SERVICE_FEE_KEY,
  );

  if (!hasFeeConfig) {
    return (
      <div className="flex items-center gap-2">
        <CircleSlash className="h-4 w-4 shrink-0 stroke-gray-500 stroke-[1.5]" />
        <label className="text-sm text-gray-500">
          La configuración de tarifas no está disponible
        </label>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-8">
      <div className="flex flex-col gap-4">
        <h3 className="text-base font-semibold text-gray-900">
          Comisión de recolección en punto
        </h3>
        <AppFeeField
          {...props}
          feeKey={APP_SERVICE_FEE_KEY}
          label="Comisión"
          hasType
        />
      </div>
      <div className="flex flex-col gap-4">
        <h3 className="text-base font-semibold text-gray-900">
          Comisión de entrega a asiento
        </h3>
        <AppFeeField
          {...props}
          feeKey={APP_DELIVERY_FEE_KEY}
          label="Comisión"
          hasType
        />
        <AppFeeField
          {...props}
          feeKey={APP_DELIVERY_FREE_THRESHOLD_KEY}
          label="Monto mínimo para entrega en asiento gratis"
          hasType={false}
          required={false}
        />
      </div>
      {emptyValueHint && (
        <p className="text-xs text-gray-500">{emptyValueHint}</p>
      )}
    </div>
  );
};
